import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { post } from '@/lib/api';
import { humanize } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { apiMessage, hasDetailPath } from './LabBenchErrors';
import { ResultInput, buildEntry, flagFor, seedDraft, type Draft, type SheetItem } from './LabBenchResultInput';

export interface AmendResponse {
  orderItemId: string;
  resultId: string;
  version: number;
  previousValue: string;
  value: string;
  flag: string;
  critical: boolean;
  label: string;
  notifiedClinicians: number;
  notifiedPatient: boolean;
}

const MIN_REASON = 5;

function noticeFor(result: AmendResponse): string {
  const told: string[] = [];
  if (result.notifiedClinicians > 0) told.push('the doctor');
  if (result.notifiedPatient) told.push('the patient');
  const who = told.join(' and ');
  const sentence =
    told.length === 0
      ? 'No SMS could be sent, so tell the doctor yourself.'
      : `${who.charAt(0).toUpperCase()}${who.slice(1)} ${told.length === 1 ? 'was' : 'were'} notified.`;
  const critical = result.critical ? ' The new value is critical and must be acknowledged.' : '';
  return `${sentence} The result now needs re-verification.${critical}`;
}

export function LabBenchAmendDialog({
  item,
  onClose,
  onAmended,
}: {
  item: SheetItem;
  onClose: () => void;
  onAmended: (result: AmendResponse) => void;
}) {
  const toast = useToast();
  const current = item.latestResult;
  const [draft, setDraft] = useState<Draft>(() => seedDraft(item));
  const [unit, setUnit] = useState(current?.unit ?? item.test.unit ?? '');
  const [reason, setReason] = useState('');
  const [qcReason, setQcReason] = useState('');
  const [needsQcReason, setNeedsQcReason] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entry = buildEntry(item, draft);
  const flag = flagFor(item, draft.value);
  const ready =
    entry.value !== '' && reason.trim().length >= MIN_REASON && (!needsQcReason || qcReason.trim().length >= MIN_REASON);

  const amend = useMutation({
    mutationFn: () =>
      post<AmendResponse>('/lab/results/amend', {
        ...entry,
        unit: unit.trim() || undefined,
        reason: reason.trim(),
        qcOverrideReason: needsQcReason ? qcReason.trim() : undefined,
      }),
    onSuccess: (result) => {
      const notice = noticeFor(result);
      if (result.critical) toast.warning(tr('lab', 'result_amended'), notice);
      else toast.success(tr('lab', 'result_amended'), notice);
      onAmended(result);
    },
    onError: (caught) => {
      setError(apiMessage(caught));
      if (hasDetailPath(caught, 'qcOverrideReason')) setNeedsQcReason(true);
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('lab', 'amend_result_x', { name: item.test.name })}
      description={current ? `On record: ${current.value}${current.unit ? ` ${current.unit}` : ''}` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              amend.mutate();
            }}
            loading={amend.isPending}
            disabled={!ready}
          >
            {tr('lab', 'amend_result')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="destructive">{error}</Alert>}
        <Alert variant="warning">
          {tr('lab', 'the_original_value_stays_on_the_record_the')}
        </Alert>

        <Field label={tr('lab', 'new_value')} required>
          <div className="flex items-start gap-2">
            <ResultInput
              item={item}
              draft={draft}
              disabled={false}
              bare
              onChange={(patch) => setDraft((was) => ({ ...was, ...patch }))}
              onKeyDown={() => undefined}
            />
            {draft.value.trim() !== '' && item.test.resultType === 'numeric' && (
              <Badge variant={flag.startsWith('critical') ? 'destructive' : flag === 'normal' ? 'success' : 'warning'} className="mt-1.5">
                {humanize(flag)}
              </Badge>
            )}
          </div>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={tr('lab', 'unit')}>
            <Input value={unit} maxLength={24} onChange={(e) => setUnit(e.target.value)} />
          </Field>
          <Field label={tr('lab', 'comment_on_the_result')}>
            <Input value={draft.comment} maxLength={1000} onChange={(e) => setDraft((was) => ({ ...was, comment: e.target.value }))} />
          </Field>
        </div>

        <Field label={tr('lab', 'reason_for_the_amendment')} required hint={tr('lab', 'at_least_x_characters_shown_on_the_report_and', { MIN_REASON: MIN_REASON })}>
          <Textarea rows={2} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
        </Field>

        {needsQcReason && (
          <Field
            label={tr('lab', 'qc_override_reason')}
            required
            hint={tr('lab', 'quality_control_has_failed_for_this_test_a')}
          >
            <Textarea rows={2} value={qcReason} maxLength={300} onChange={(e) => setQcReason(e.target.value)} />
          </Field>
        )}
      </div>
    </Dialog>
  );
}
